import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Scale, Trash2, Plus, Upload, X, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fixImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('data:')) return url;
  if (url.includes('emergentagent.com/api/') || url.includes('getmealguide.com/api/')) {
    const pathMatch = url.match(/\/api\/uploads\/.+$/);
    if (pathMatch) {
      return `${BACKEND_URL}${pathMatch[0]}`;
    }
  }
  return url;
};

export default function RecipeEditorModal({ recipeId, open, onClose, onRecipeUpdated, recipeIds = [], onNavigate }) {
  const [recipe, setRecipe] = useState(null);
  const [foodBankItems, setFoodBankItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Navigation
  const currentIndex = recipeIds.indexOf(recipeId);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < recipeIds.length - 1;
  
  const goToPrev = useCallback(() => {
    if (hasPrev && onNavigate) {
      onNavigate(recipeIds[currentIndex - 1]);
    }
  }, [hasPrev, onNavigate, recipeIds, currentIndex]);
  
  const goToNext = useCallback(() => {
    if (hasNext && onNavigate) {
      onNavigate(recipeIds[currentIndex + 1]);
    }
  }, [hasNext, onNavigate, recipeIds, currentIndex]);
  
  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    
    const handleKeyDown = (e) => {
      // Don't navigate if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      if (e.key === 'ArrowLeft' && hasPrev) {
        e.preventDefault();
        goToPrev();
      } else if (e.key === 'ArrowRight' && hasNext) {
        e.preventDefault();
        goToNext();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, hasPrev, hasNext, goToPrev, goToNext]);
  
  // Section collapse state
  const [sectionsExpanded, setSectionsExpanded] = useState({
    ingredients: true,
    essentials: false,
    instructions: false
  });
  
  // Image Upload
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open && recipeId) {
      fetchRecipe();
      fetchFoodBank();
    }
  }, [recipeId, open]);

  const fetchRecipe = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/library-recipes/${recipeId}`);
      setRecipe(response.data);
    } catch (error) {
      toast.error('Failed to load recipe');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const fetchFoodBank = async () => {
    try {
      const response = await axios.get(`${API}/food-bank?limit=500`);
      const items = response.data.foods || response.data || [];
      setFoodBankItems(items);
    } catch (error) {
      console.error('Failed to fetch Food Bank:', error);
    }
  };

  const updateIngredientGrams = async (ingredientIndex, newGrams) => {
    try {
      const ingredient = recipe.ingredients[ingredientIndex];
      const gramsValue = parseFloat(newGrams);
      
      if (isNaN(gramsValue) || gramsValue <= 0) {
        toast.error('Please enter a valid positive number');
        return;
      }
      
      const response = await axios.put(
        `${API}/library-recipes/${recipeId}/ingredients/${ingredientIndex}`,
        { food_bank_id: ingredient.food_bank_id, grams: gramsValue }
      );

      const updatedRecipe = { ...recipe };
      updatedRecipe.ingredients[ingredientIndex] = response.data.ingredient;
      updatedRecipe.total_kcal = response.data.recipe_totals.kcal;
      updatedRecipe.total_protein = response.data.recipe_totals.protein_g;
      updatedRecipe.total_carbs = response.data.recipe_totals.carbs_g;
      updatedRecipe.total_fat = response.data.recipe_totals.fat_g;
      
      setRecipe(updatedRecipe);
      onRecipeUpdated?.(updatedRecipe);
    } catch (error) {
      toast.error('Failed to update ingredient');
    }
  };

  const swapIngredient = async (ingredientIndex, newFoodBankId) => {
    try {
      const currentGrams = recipe.ingredients[ingredientIndex].grams;
      
      const response = await axios.put(
        `${API}/library-recipes/${recipeId}/ingredients/${ingredientIndex}`,
        { food_bank_id: newFoodBankId, grams: currentGrams }
      );

      const updatedRecipe = { ...recipe };
      updatedRecipe.ingredients[ingredientIndex] = response.data.ingredient;
      updatedRecipe.total_kcal = response.data.recipe_totals.kcal;
      updatedRecipe.total_protein = response.data.recipe_totals.protein_g;
      updatedRecipe.total_carbs = response.data.recipe_totals.carbs_g;
      updatedRecipe.total_fat = response.data.recipe_totals.fat_g;
      updatedRecipe.allergens = response.data.allergens || recipe.allergens || [];
      
      setRecipe(updatedRecipe);
      onRecipeUpdated?.(updatedRecipe);
      toast.success('Ingredient swapped');
    } catch (error) {
      toast.error('Failed to swap ingredient');
    }
  };

  const addIngredient = async (foodBankId, grams) => {
    try {
      const response = await axios.post(
        `${API}/library-recipes/${recipeId}/ingredients`,
        { food_bank_id: foodBankId, grams: parseFloat(grams) }
      );

      const updatedRecipe = { ...recipe };
      updatedRecipe.ingredients.push(response.data.ingredient);
      updatedRecipe.allergens = response.data.allergens || recipe.allergens || [];
      updatedRecipe.total_kcal = response.data.recipe_totals.kcal;
      updatedRecipe.total_protein = response.data.recipe_totals.protein_g;
      updatedRecipe.total_carbs = response.data.recipe_totals.carbs_g;
      updatedRecipe.total_fat = response.data.recipe_totals.fat_g;
      
      setRecipe(updatedRecipe);
      onRecipeUpdated?.(updatedRecipe);
      toast.success('Ingredient added');
    } catch (error) {
      toast.error('Failed to add ingredient');
    }
  };

  const deleteIngredient = async (ingredientIndex) => {
    try {
      const response = await axios.delete(
        `${API}/library-recipes/${recipeId}/ingredients/${ingredientIndex}`
      );

      const updatedRecipe = { ...recipe };
      updatedRecipe.ingredients.splice(ingredientIndex, 1);
      updatedRecipe.allergens = response.data.allergens || recipe.allergens || [];
      updatedRecipe.total_kcal = response.data.recipe_totals.kcal;
      updatedRecipe.total_protein = response.data.recipe_totals.protein_g;
      updatedRecipe.total_carbs = response.data.recipe_totals.carbs_g;
      updatedRecipe.total_fat = response.data.recipe_totals.fat_g;
      
      setRecipe(updatedRecipe);
      onRecipeUpdated?.(updatedRecipe);
      toast.success('Ingredient removed');
    } catch (error) {
      toast.error('Failed to remove ingredient');
    }
  };

  const updateCookingTime = async (value) => {
    try {
      await axios.put(`${API}/library-recipes/${recipeId}/metadata`, {
        cooking_time: value
      });
      setRecipe({ ...recipe, cooking_time: value, estimated_time: value });
      onRecipeUpdated?.({ ...recipe, cooking_time: value, estimated_time: value });
      toast.success('Cooking time updated');
    } catch (error) {
      toast.error('Failed to update cooking time');
    }
  };

  const updateEssential = async (idx, foodBankId, grams) => {
    try {
      const response = await axios.put(
        `${API}/library-recipes/${recipeId}/cooking-essentials/${idx}`,
        { food_bank_id: foodBankId, grams: grams }
      );
      const updatedRecipe = { ...recipe };
      updatedRecipe.cooking_essentials[idx] = response.data.essential;
      updatedRecipe.total_kcal = response.data.recipe_totals.kcal;
      updatedRecipe.total_protein = response.data.recipe_totals.protein_g;
      updatedRecipe.total_carbs = response.data.recipe_totals.carbs_g;
      updatedRecipe.total_fat = response.data.recipe_totals.fat_g;
      setRecipe(updatedRecipe);
      onRecipeUpdated?.(updatedRecipe);
      toast.success('Essential updated');
    } catch (error) {
      toast.error('Failed to update essential');
    }
  };

  const addEssential = async (foodBankId, grams) => {
    try {
      const response = await axios.post(
        `${API}/library-recipes/${recipeId}/cooking-essentials`,
        { food_bank_id: foodBankId, grams: grams }
      );
      const updatedRecipe = { ...recipe };
      if (!updatedRecipe.cooking_essentials) updatedRecipe.cooking_essentials = [];
      updatedRecipe.cooking_essentials.push(response.data.essential);
      updatedRecipe.total_kcal = response.data.recipe_totals.kcal;
      updatedRecipe.total_protein = response.data.recipe_totals.protein_g;
      updatedRecipe.total_carbs = response.data.recipe_totals.carbs_g;
      updatedRecipe.total_fat = response.data.recipe_totals.fat_g;
      setRecipe(updatedRecipe);
      onRecipeUpdated?.(updatedRecipe);
      toast.success('Essential added');
    } catch (error) {
      toast.error('Failed to add essential');
    }
  };

  const deleteEssential = async (idx) => {
    try {
      const response = await axios.delete(`${API}/library-recipes/${recipeId}/cooking-essentials/${idx}`);
      const updatedRecipe = { ...recipe };
      updatedRecipe.cooking_essentials.splice(idx, 1);
      updatedRecipe.total_kcal = response.data.recipe_totals.kcal;
      updatedRecipe.total_protein = response.data.recipe_totals.protein_g;
      updatedRecipe.total_carbs = response.data.recipe_totals.carbs_g;
      updatedRecipe.total_fat = response.data.recipe_totals.fat_g;
      setRecipe(updatedRecipe);
      onRecipeUpdated?.(updatedRecipe);
      toast.success('Essential removed');
    } catch (error) {
      toast.error('Failed to remove essential');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/library-recipes/${recipeId}/upload-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setRecipe({ ...recipe, image_url: response.data.image_url });
      onRecipeUpdated?.({ ...recipe, image_url: response.data.image_url });
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const updateInstructions = async () => {
    try {
      await axios.put(`${API}/library-recipes/${recipeId}/metadata`, {
        instructions: recipe.instructions
      });
      onRecipeUpdated?.(recipe);
      toast.success('Instructions saved!');
    } catch (error) {
      toast.error('Failed to save instructions');
    }
  };

  const toggleSection = (section) => {
    setSectionsExpanded(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
        {/* Navigation Arrows - positioned outside the modal */}
        {recipeIds.length > 1 && (
          <>
            {/* Left Arrow - calc positions it just outside the modal */}
            <Button
              variant="outline"
              size="icon"
              className={`fixed top-1/2 -translate-y-1/2 z-[210] h-12 w-12 rounded-full bg-white shadow-lg border-2 ${!hasPrev ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-100'}`}
              style={{ left: 'calc(50% - 480px)' }}
              onClick={goToPrev}
              disabled={!hasPrev}
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            
            {/* Right Arrow */}
            <Button
              variant="outline"
              size="icon"
              className={`fixed top-1/2 -translate-y-1/2 z-[210] h-12 w-12 rounded-full bg-white shadow-lg border-2 ${!hasNext ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-100'}`}
              style={{ right: 'calc(50% - 480px)' }}
              onClick={goToNext}
              disabled={!hasNext}
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </>
        )}
        
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <p>Loading recipe...</p>
          </div>
        ) : recipe ? (
          <div className="flex flex-col">
            {/* Header with Image and Macros */}
            <div className="sticky top-0 bg-white z-10 border-b">
              {/* Position Counter */}
              {recipeIds.length > 1 && (
                <div className="absolute top-2 right-12 text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
                  {currentIndex + 1} / {recipeIds.length}
                </div>
              )}
              <DialogHeader className="p-4 pb-0">
                <div className="flex items-start gap-4">
                  {/* Recipe Image */}
                  <div className="relative w-32 h-32 flex-shrink-0 bg-gray-100 rounded-lg flex items-center justify-center">
                    {recipe.image_url ? (
                      <img 
                        src={fixImageUrl(recipe.image_url)} 
                        alt={recipe.name}
                        className="w-full h-full object-contain rounded-lg"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-xs">
                        No image
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploading}
                      className="hidden"
                      id="modal-image-upload"
                    />
                    <Button
                      size="sm"
                      variant="secondary"
                      className="absolute -bottom-2 -right-2 h-7 w-7 p-0 rounded-full"
                      onClick={() => document.getElementById('modal-image-upload').click()}
                      disabled={uploading}
                    >
                      <Upload className="w-3 h-3" />
                    </Button>
                  </div>
                  
                  {/* Title and Info */}
                  <div className="flex-1 min-w-0">
                    <Input
                      value={recipe.name}
                      onChange={(e) => setRecipe({...recipe, name: e.target.value})}
                      onBlur={async () => {
                        try {
                          await axios.patch(`${API}/library-recipes/${recipeId}/name`, { name: recipe.name });
                          onRecipeUpdated?.(recipe);
                        } catch (error) {
                          toast.error('Failed to update name');
                        }
                      }}
                      className="text-xl font-bold border-none shadow-none px-0 h-8 focus:ring-0"
                    />
                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                      <Badge variant="outline">{recipe.meal_type}</Badge>
                      <Select
                        value={recipe.cooking_time || recipe.estimated_time || ''}
                        onValueChange={updateCookingTime}
                      >
                        <SelectTrigger className="h-6 w-28 text-xs">
                          <SelectValue placeholder="Time">
                            ⏱️ {recipe.cooking_time || recipe.estimated_time || 'Set time'}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5-15">5-15 min</SelectItem>
                          <SelectItem value="15-30">15-30 min</SelectItem>
                          <SelectItem value="30+">30+ min</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </DialogHeader>
              
              {/* Macro Summary */}
              <div className="grid grid-cols-4 gap-2 p-4 pt-3 text-center text-sm">
                <div className="bg-blue-50 rounded-lg p-2">
                  <p className="text-lg font-bold text-blue-600">{Math.round(recipe.total_kcal)}</p>
                  <p className="text-xs text-gray-500">Calories</p>
                </div>
                <div className="bg-green-50 rounded-lg p-2">
                  <p className="text-lg font-bold text-green-600">{Math.round(recipe.total_protein)}g</p>
                  <p className="text-xs text-gray-500">Protein</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-2">
                  <p className="text-lg font-bold text-amber-600">{Math.round(recipe.total_carbs)}g</p>
                  <p className="text-xs text-gray-500">Carbs</p>
                </div>
                <div className="bg-red-50 rounded-lg p-2">
                  <p className="text-lg font-bold text-red-600">{Math.round(recipe.total_fat)}g</p>
                  <p className="text-xs text-gray-500">Fat</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 space-y-3">
              {/* Ingredients Section */}
              <div className="border rounded-lg">
                <button 
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50"
                  onClick={() => toggleSection('ingredients')}
                >
                  <span className="font-semibold flex items-center gap-2">
                    Ingredients ({recipe.ingredients?.length || 0})
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        const foodId = foodBankItems[0]?.id;
                        if (foodId) addIngredient(foodId, 100);
                      }}
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  </span>
                  {sectionsExpanded.ingredients ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                
                {sectionsExpanded.ingredients && (
                  <div className="p-3 pt-0 space-y-2">
                    {recipe.ingredients?.map((ing, idx) => {
                      // Determine ingredient roles (support both old single ID and new array formats)
                      const ingId = String(ing.food_bank_id);
                      
                      // Helper to check if ingredient is in a role (supports both formats)
                      const getIds = (field, arrayField) => {
                        if (recipe[arrayField] && Array.isArray(recipe[arrayField])) {
                          return recipe[arrayField].map(id => String(id));
                        }
                        if (recipe[field]) {
                          return [String(recipe[field]), String(Math.floor(parseFloat(recipe[field])))];
                        }
                        return [];
                      };
                      
                      const proteinIds = getIds('protein_source_id', 'protein_source_ids');
                      const primaryIds = getIds('primary_scaler_id', 'primary_scaler_ids');
                      const secondaryIds = getIds('secondary_scaler_id', 'secondary_scaler_ids');
                      
                      const isProtein = proteinIds.includes(ingId);
                      const isPrimary = primaryIds.includes(ingId);
                      const isSecondary = secondaryIds.includes(ingId);
                      
                      // Build role badges array
                      const roles = [];
                      if (isProtein) roles.push({ key: 'protein', label: '🥩', color: 'bg-purple-100 text-purple-700 border-purple-300' });
                      if (isPrimary) roles.push({ key: 'primary', label: '🍚', color: 'bg-blue-100 text-blue-700 border-blue-300' });
                      if (isSecondary) roles.push({ key: 'secondary', label: '🧀', color: 'bg-amber-100 text-amber-700 border-amber-300' });
                      
                      // Toggle role function
                      const toggleRole = async (roleType) => {
                        try {
                          const fieldMap = {
                            protein: 'protein_source_ids',
                            primary: 'primary_scaler_ids',
                            secondary: 'secondary_scaler_ids'
                          };
                          const currentIdsMap = {
                            protein: [...new Set(proteinIds.filter(id => id && id !== 'NaN'))],
                            primary: [...new Set(primaryIds.filter(id => id && id !== 'NaN'))],
                            secondary: [...new Set(secondaryIds.filter(id => id && id !== 'NaN'))]
                          };
                          
                          const field = fieldMap[roleType];
                          const currentIds = currentIdsMap[roleType];
                          const isCurrentlySet = currentIds.includes(ingId);
                          
                          let newIds;
                          if (isCurrentlySet) {
                            // Remove from role
                            newIds = currentIds.filter(id => id !== ingId);
                          } else {
                            // Add to role (deduplicated)
                            newIds = [...new Set([...currentIds, ingId])];
                          }
                          
                          const updates = { [field]: newIds };
                          await axios.put(`${API}/library-recipes/${recipeId}/metadata`, updates);
                          setRecipe({ ...recipe, [field]: newIds });
                          onRecipeUpdated?.({ ...recipe, [field]: newIds });
                          toast.success(isCurrentlySet ? 'Role removed' : 'Role added');
                        } catch (error) {
                          toast.error('Failed to update role');
                        }
                      };
                      
                      return (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                          {/* Role Toggle Buttons */}
                          <div className="flex gap-0.5">
                            <button
                              onClick={() => toggleRole('protein')}
                              className={`w-6 h-6 text-xs rounded flex items-center justify-center border transition-all ${
                                isProtein 
                                  ? 'bg-purple-100 text-purple-700 border-purple-400' 
                                  : 'bg-gray-100 text-gray-400 border-gray-200 hover:bg-purple-50 hover:border-purple-200'
                              }`}
                              title="Toggle Protein role"
                            >
                              🥩
                            </button>
                            <button
                              onClick={() => toggleRole('primary')}
                              className={`w-6 h-6 text-xs rounded flex items-center justify-center border transition-all ${
                                isPrimary 
                                  ? 'bg-blue-100 text-blue-700 border-blue-400' 
                                  : 'bg-gray-100 text-gray-400 border-gray-200 hover:bg-blue-50 hover:border-blue-200'
                              }`}
                              title="Toggle Primary Scaler role"
                            >
                              🍚
                            </button>
                            <button
                              onClick={() => toggleRole('secondary')}
                              className={`w-6 h-6 text-xs rounded flex items-center justify-center border transition-all ${
                                isSecondary 
                                  ? 'bg-amber-100 text-amber-700 border-amber-400' 
                                  : 'bg-gray-100 text-gray-400 border-gray-200 hover:bg-amber-50 hover:border-amber-200'
                              }`}
                              title="Toggle Secondary Scaler role"
                            >
                              🧀
                            </button>
                          </div>
                          
                          {/* Food Item Selector */}
                          <Select
                            value={ing.food_bank_id}
                            onValueChange={(value) => swapIngredient(idx, value)}
                          >
                            <SelectTrigger className="flex-1 h-8 text-sm">
                              <SelectValue>{ing.food_bank_name || 'Select'}</SelectValue>
                            </SelectTrigger>
                            <SelectContent className="max-h-[200px]">
                              {foodBankItems.map(item => (
                                <SelectItem key={item.id} value={item.id} className="text-sm">
                                  {item.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            value={ing.grams}
                            onChange={(e) => {
                              const updated = [...recipe.ingredients];
                              updated[idx] = { ...ing, grams: parseFloat(e.target.value) || 0 };
                              setRecipe({ ...recipe, ingredients: updated });
                            }}
                            onBlur={(e) => updateIngredientGrams(idx, e.target.value)}
                            className="w-20 h-8 text-sm"
                          />
                          <span className="text-xs text-gray-500 w-6">g</span>
                          <span className="text-xs text-gray-600 w-14">{Math.round(ing.kcal)} cal</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                            onClick={() => deleteIngredient(idx)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      );
                    })}
                    <p className="text-xs text-gray-400 mt-2">
                      Click icons to toggle roles. Items can have multiple roles. 🥩 = Protein | 🍚 = Primary | 🧀 = Secondary
                    </p>
                  </div>
                )}
              </div>

              {/* Cooking Essentials Section */}
              <div className="border rounded-lg border-dashed">
                <button 
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50"
                  onClick={() => toggleSection('essentials')}
                >
                  <span className="font-semibold text-gray-600 flex items-center gap-2">
                    Cooking Essentials ({recipe.cooking_essentials?.length || 0})
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        const foodId = foodBankItems[0]?.id;
                        if (foodId) addEssential(foodId, 1);
                      }}
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  </span>
                  {sectionsExpanded.essentials ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                
                {sectionsExpanded.essentials && (
                  <div className="p-3 pt-0 space-y-2">
                    {(!recipe.cooking_essentials || recipe.cooking_essentials.length === 0) ? (
                      <p className="text-sm text-gray-400 italic">No cooking essentials</p>
                    ) : (
                      recipe.cooking_essentials.map((ess, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                          <Select
                            value={ess.food_bank_id}
                            onValueChange={(value) => updateEssential(idx, value, ess.quantity || ess.grams || 1)}
                          >
                            <SelectTrigger className="flex-1 h-8 text-sm">
                              <SelectValue>{ess.food_bank_name || 'Select'}</SelectValue>
                            </SelectTrigger>
                            <SelectContent className="max-h-[200px]">
                              {foodBankItems.map(item => (
                                <SelectItem key={item.id} value={item.id} className="text-sm">
                                  {item.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            value={ess.quantity || ess.grams || 0}
                            onChange={(e) => {
                              const updated = [...recipe.cooking_essentials];
                              updated[idx] = { ...ess, quantity: parseFloat(e.target.value) || 0 };
                              setRecipe({ ...recipe, cooking_essentials: updated });
                            }}
                            onBlur={(e) => updateEssential(idx, ess.food_bank_id, parseFloat(e.target.value) || 1)}
                            className="w-16 h-8 text-sm"
                          />
                          <span className="text-xs text-gray-500 w-12">{ess.unit || 'g'}</span>
                          <span className="text-xs text-gray-600 w-14">{Math.round(ess.kcal || 0)} cal</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                            onClick={() => deleteEssential(idx)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))
                    )}
                    <p className="text-xs text-gray-400 mt-1">These don't scale with calorie targets</p>
                  </div>
                )}
              </div>

              {/* Instructions Section */}
              <div className="border rounded-lg">
                <button 
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50"
                  onClick={() => toggleSection('instructions')}
                >
                  <span className="font-semibold">Instructions</span>
                  {sectionsExpanded.instructions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                
                {sectionsExpanded.instructions && (
                  <div className="p-3 pt-0">
                    <textarea
                      value={recipe.instructions || ''}
                      onChange={(e) => setRecipe({ ...recipe, instructions: e.target.value })}
                      placeholder="Enter cooking instructions..."
                      className="w-full min-h-[120px] p-2 text-sm border rounded resize-y"
                      rows={5}
                    />
                    <div className="flex justify-end mt-2">
                      <Button size="sm" onClick={updateInstructions}>
                        Save Instructions
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Allergens */}
              {recipe.allergens && recipe.allergens.length > 0 && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500">Allergens:</span>
                  {recipe.allergens.map((a, i) => (
                    <Badge key={i} variant="destructive" className="text-xs">{a}</Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
